import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiFetch, fmtDate, STATUS_COLORS, PRIORITY_COLORS } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Download, Search } from "lucide-react";
import { useTaxonomy } from "@/lib/taxonomy";

export default function OSList() {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const { sectors, disciplines } = useTaxonomy();
  const [q, setQ] = useState("");
  const [filters, setFilters] = useState({ status: "all", sector: "all", discipline: "all", maintenance_type: "all" });

  const load = useCallback(() => {
    const params = {};
    for (const [k, v] of Object.entries(filters)) if (v && v !== "all") params[k] = v;
    api.get("/os", { params }).then((r) => setItems(r.data));
  }, [filters]);
  useEffect(() => { load(); }, [load]);

  const filtered = items.filter((o) => {
    if (!q) return true;
    const s = q.toLowerCase();
    return (
      String(o.number).includes(s) ||
      o.equipment_name.toLowerCase().includes(s) ||
      o.equipment_tag.toLowerCase().includes(s) ||
      o.requester_name.toLowerCase().includes(s) ||
      (o.description || "").toLowerCase().includes(s)
    );
  });

  const exportCsv = async () => {
    const res = await apiFetch("/reports/os.csv");
    const blob = await res.blob();
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "os_relatorio.csv";
    link.click();
  };

  return (
    <div className="space-y-6" data-testid="os-list">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <div className="text-[10px] tracking-[0.2em] uppercase font-semibold text-muted-foreground">Gestão</div>
          <h1 className="font-heading text-4xl font-black tracking-tight">Ordens de Serviço</h1>
        </div>
        <div className="flex gap-2">
          <Button onClick={exportCsv} variant="outline" data-testid="export-csv" className="rounded-sm gap-2">
            <Download className="w-4 h-4" /> Exportar CSV
          </Button>
          <Button onClick={() => nav("/os/nova")} data-testid="new-os-btn" className="rounded-sm bg-orange-500 hover:bg-orange-600 text-white gap-2">
            <Plus className="w-4 h-4" /> Nova OS
          </Button>
        </div>
      </div>

      <Card className="rounded-sm shadow-none p-4">
        <div className="grid md:grid-cols-5 gap-3">
          <div className="relative md:col-span-2">
            <Search className="w-4 h-4 absolute left-3 top-3 text-muted-foreground" />
            <Input placeholder="Buscar OS, TAG, equipamento..." value={q} onChange={(e) => setQ(e.target.value)} className="pl-9 rounded-sm" data-testid="search-input" />
          </div>
          <Select value={filters.status} onValueChange={(v) => setFilters({ ...filters, status: v })}>
            <SelectTrigger className="rounded-sm" data-testid="filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos os status</SelectItem>
              <SelectItem value="Aberta">Aberta</SelectItem>
              <SelectItem value="Em Andamento">Em Andamento</SelectItem>
              <SelectItem value="Concluída">Concluída</SelectItem>
              <SelectItem value="Cancelada">Cancelada</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filters.sector} onValueChange={(v) => setFilters({ ...filters, sector: v })}>
            <SelectTrigger className="rounded-sm" data-testid="filter-sector"><SelectValue placeholder="Setor" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos os setores</SelectItem>
              {sectors.map((s) => <SelectItem key={s.id} value={s.name}>{s.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filters.discipline} onValueChange={(v) => setFilters({ ...filters, discipline: v })}>
            <SelectTrigger className="rounded-sm" data-testid="filter-discipline"><SelectValue placeholder="Disciplina" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas disciplinas</SelectItem>
              {disciplines.map((d) => <SelectItem key={d.id} value={d.name}>{d.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </Card>

      <Card className="rounded-sm shadow-none overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-20">OS</TableHead>
              <TableHead>Equipamento</TableHead>
              <TableHead className="hidden md:table-cell">Setor</TableHead>
              <TableHead className="hidden md:table-cell">Disciplina</TableHead>
              <TableHead className="hidden lg:table-cell">Tipo</TableHead>
              <TableHead>Prioridade</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="hidden lg:table-cell">Aberto em</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.length === 0 ? (
              <TableRow><TableCell colSpan={8} className="text-center text-muted-foreground py-10">Nenhuma OS encontrada</TableCell></TableRow>
            ) : filtered.map((o) => (
              <TableRow key={o.id} onClick={() => nav(`/os/${o.id}`)} className="cursor-pointer hover:bg-secondary" data-testid={`os-row-${o.number}`}>
                <TableCell className="font-mono-tag font-semibold">#{o.number}</TableCell>
                <TableCell>
                  <div className="font-medium">{o.equipment_name}</div>
                  <div className="text-xs font-mono-tag text-muted-foreground">{o.equipment_tag}</div>
                </TableCell>
                <TableCell className="hidden md:table-cell">{o.sector}</TableCell>
                <TableCell className="hidden md:table-cell">{o.discipline}</TableCell>
                <TableCell className="hidden lg:table-cell">{o.maintenance_type}</TableCell>
                <TableCell><Badge variant="outline" className={`rounded-sm ${PRIORITY_COLORS[o.priority]}`}>{o.priority}</Badge></TableCell>
                <TableCell><Badge variant="outline" className={`rounded-sm ${STATUS_COLORS[o.status]}`}>{o.status}</Badge></TableCell>
                <TableCell className="hidden lg:table-cell text-xs text-muted-foreground">{fmtDate(o.created_at)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
